// --------------------------------------------------------------
// Created On: 2025-06-24
// Author: Dimitra Weinstein
//
// Last Modified: 2025-12-11
// Modified By: Dimitra Weinstein
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------
import React, { useRef, useState } from "react";
import useApi from "../../../hooks/useApi";
import { API } from "../../../constants/miscellaneous";
import getApiError from "../../../utilities/api/getApiError";
import Modal from "../../../components/Modal/Modal";
import ModalHeader from "../../../components/ModalHeader/ModalHeader";
import ModalBody from "../../../components/ModalBody/ModalBody";
import ModalFooter from "../../../components/ModalFooter/ModalFooter";
import SalesQuoteDetails from "../../../components/SalesQuoteDetails/SalesQuoteDetails";
import formatDateLocal from "../../../utilities/time/formatDateLocal";
import formatDashTimeString from "../../../utilities/formatDashTimeString";
import nowToUtcIso from "../../../utilities/time/nowToUtcIso";
import { useNavigate } from "react-router-dom";

// Displays the details of a sales quote in a modal when a quote is selected from the list.
export default function QuoteListModal(props: Props): Component {
  const [allowPartialShipments, setAllowPartialShipments] = useState<boolean>(false);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [comment, setComment] = useState<string>("");
  const [companyDiscount, setCompanyDiscount] = useState<number>(0.0);
  const [distributorName, setDistributorName] = useState<string | null>(null);
  const [grandTotal, setGrandTotal] = useState<number>(0.0);
  const [subTotal, setSubTotal] = useState<number>(0.0);
  const [poNumber, setPoNumber] = useState<string>("");
  const [orderNumber, setOrderNumber] = useState<number | null>(null);
  const [quoteName, setQuoteName] = useState<string>("");
  const [shippingAddressLine1, setShippingAddressLine1] = useState<string>("");
  const [shippingAddressLine2, setShippingAddressLine2] = useState<string | null>(null);
  const [shippingCity, setShippingCity] = useState<string>("");
  const [shippingCompanyName, setShippingCompanyName] = useState<string>("");
  const [shippingContactName, setShippingContactName] = useState<string>("");
  const [shippingCountry, setShippingCountry] = useState<string>("");
  const [shippingPhoneNumber, setShippingPhoneNumber] = useState<string>("");
  const [shippingState, setShippingState] = useState<string>("");
  const [shippingZipCode, setShippingZipCode] = useState<number>(0);
  const [shippingBillingType, setShippingBillingType] = useState<string | null>(null);
  const [shippingBillingAccountNumber, setShippingBillingAccountNumber] = useState<string | null>(null);
  const [specialInstructions, setSpecialInstructions] = useState<string | null>(null);
  const [paintColors, setPaintColors] = useState<PaintColor[]>([]);

  const salesQuoteDetailsRef = useRef<{ print: () => void } | null>(null);
  const navigate = useNavigate();

  // Retrieve paint colors from database.
  useApi(
    () => {
      return true;
    },
    {
      url: `${API}/paint-color`,
      method: "GET",
    },
    async (response: Response, responseBody: PaintColorResponseBody) => {
      if (response.ok && responseBody) {
        setPaintColors(responseBody.paintColors);
      } else {
        props.onChangeErrorMessage(await getApiError(response, "Error loading paint colors."));
      }
    },
    []
  );

  // Retrieves the sales quote details when the modal is opened.
  useApi(
    () => {
      props.onChangeLoading(true);
      props.showModal === true;
      return true;
    },
    {
      url: `${API}/sales-quote/${props.selectedQuoteId}`,
      method: "GET",
    },
    async (response: Response, responseBody: ResponseBody) => {
      if (response.ok && responseBody) {
        setAllowPartialShipments(responseBody.allowPartialShipments);
        setCart(responseBody.cart);
        setComment(responseBody.comment === null ? "" : responseBody.comment);
        setCompanyDiscount(responseBody.companyDiscount === null ? 0.0 : responseBody.companyDiscount);
        setDistributorName(responseBody.distributorName === null ? "" : responseBody.distributorName);
        setGrandTotal(responseBody.grandTotal);
        setSubTotal(responseBody.subTotal);
        setPoNumber(responseBody.poNumber);
        setOrderNumber(responseBody.orderNumber);
        setQuoteName(responseBody.quoteName);
        setShippingAddressLine1(responseBody.shippingAddressLine1);
        setShippingAddressLine2(responseBody.shippingAddressLine2);
        setShippingCity(responseBody.shippingCity);
        setShippingCompanyName(responseBody.shippingCompanyName);
        setShippingContactName(responseBody.shippingContactName);
        setShippingCountry(responseBody.shippingCountry);
        setShippingPhoneNumber(responseBody.shippingPhoneNumber);
        setShippingState(responseBody.shippingState);
        setShippingZipCode(responseBody.shippingZipCode);
        setShippingBillingType(responseBody.shippingBillingType);
        setShippingBillingAccountNumber(responseBody.shippingBillingAccountNumber);
        setSpecialInstructions(responseBody.specialInstructions);
        props.onChangeFailedToLoad(false);
        props.onChangeErrorMessage("");
      } else {
        props.onChangeFailedToLoad(true);
        props.onChangeErrorMessage(await getApiError(response, "Unable to retrieve sales quote details."));
      }
      props.onChangeLoading(false);
    },
    [props.showModal]
  );

  // Exits the modal and resets the state.
  function exitModal(): void {
    props.onChangeFailedToLoad(false);
    props.onChangeErrorMessage("");
    props.onClose();
  }

  // Formats the quote created date by removing the time from the string then converting it into a slash format.
  function formatSalesQuoteDate(date: string | null): string {
    if (date !== null && date.trim() !== "") {
      let dateString = formatDateLocal(date);
      const newDate = new Date(dateString);
      dateString = newDate.toISOString().split("T")[0];
      return formatDashTimeString(dateString);
    } else {
      return "";
    }
  }

  // Formats the quote date by removing the time from the string, adding 30 days, and then converting it into a slash format. This is used to display the expiration date of the quote.
  function formatSalesQuoteDateAndAddThirtyDays(date: string | null): string {
    if (date !== null && date.trim() !== "") {
      let dateString = formatDateLocal(date);
      const newDate = new Date(dateString);
      newDate.setDate(newDate.getDate() + 30);
      dateString = newDate.toISOString().split("T")[0];
      return formatDashTimeString(dateString);
    } else {
      return "";
    }
  }

  // Function to check if the quote is expired.
  function quoteIsExpired(quote: SalesQuote): boolean {
    const thirtyDaysAgo = nowToUtcIso(30);
    return quote.createdUtc <= thirtyDaysAgo;
  }

  return (
    <Modal
      show={props.showModal}
      onHide={() => exitModal()}
      backdropClassName=""
      style={{ zIndex: "var(--standard-modal-z-index)" }}
      size="xl"
      centered
      animation
    >
      <ModalHeader>
        <h5 className="font-weight-bold">Quote Details</h5>
        {props.quote.receivedUtc !== null && (
          <span className="bg-success text-white">
            <div className="p-2">Ordered: {formatSalesQuoteDate(props.quote.receivedUtc)}</div>
          </span>
        )}
        {quoteIsExpired(props.quote) && (
          <span className="bg-danger text-white">
            <div className="p-2">Expired: {formatSalesQuoteDateAndAddThirtyDays(props.quote.createdUtc)}</div>
          </span>
        )}
      </ModalHeader>

      <ModalBody>
        <SalesQuoteDetails
          ref={salesQuoteDetailsRef}
          paintColors={paintColors}
          loading={props.loading}
          salesQuoteId={props.selectedQuoteId}
          allowPartialShipments={allowPartialShipments}
          cart={cart}
          comment={comment}
          companyDiscount={companyDiscount}
          distributorName={distributorName}
          grandTotal={grandTotal}
          subTotal={subTotal}
          poNumber={poNumber}
          orderNumber={orderNumber}
          quoteName={quoteName}
          shippingAddressLine1={shippingAddressLine1}
          shippingAddressLine2={shippingAddressLine2}
          shippingCity={shippingCity}
          shippingCompanyName={shippingCompanyName}
          shippingContactName={shippingContactName}
          shippingCountry={shippingCountry}
          shippingPhoneNumber={shippingPhoneNumber}
          shippingState={shippingState}
          shippingZipCode={shippingZipCode}
          shippingBillingType={shippingBillingType}
          shippingBillingAccountNumber={shippingBillingAccountNumber}
          specialInstructions={specialInstructions}
          onChangeErrorMessage={(errorMessage: string) => props.onChangeErrorMessage(errorMessage)}
          onChangeLoading={(loading: boolean) => props.onChangeLoading(loading)}
          onChangeFailedToLoad={(failedToLoad: boolean) => props.onChangeFailedToLoad(failedToLoad)}
        />
      </ModalBody>

      <ModalFooter className="d-flex flex-row justify-content-between">
        <button
          className="btn btn-primary"
          type="button"
          onClick={() => {
            salesQuoteDetailsRef.current?.print();
          }}
        >
          Print Sales Quote
        </button>
        <div>
          {props.quote.receivedUtc === null && !quoteIsExpired(props.quote) && (
            <button
              data-test="sales-quote-submit-order-button"
              className="btn btn-primary mx-3"
              onClick={() => navigate(`/submit-order/quote/${props.quote.salesQuoteId}`)}
            >
              Submit Order
            </button>
          )}
          <button className="btn btn-primary" type="button" onClick={() => exitModal()}>
            Close
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
}

interface Props {
  loading: boolean;
  showModal: boolean;
  selectedQuoteId: number;
  quote: SalesQuote;
  onClose: () => void;
  onChangeLoading: (loading: boolean) => void;
  onChangeFailedToLoad: (failedToLoad: boolean) => void;
  onChangeErrorMessage: (errorMessage: string) => void;
}

interface CartItem {
  partId: number;
  partNumber: string;
  quantity: number;
  paintColorId: number | null;
  impellerTrim: string | null;
  impellerAngle: string | null;
  price: number;
  description: string;
}

interface ResponseBody {
  quoteName: string;
  poNumber: string;
  orderNumber: number | null;
  cart: CartItem[];
  companyDiscount: number | null;
  distributorName: string | null;
  grandTotal: number;
  shippingCountry: string;
  shippingState: string;
  shippingCity: string;
  shippingZipCode: number;
  shippingAddressLine1: string;
  shippingAddressLine2: string | null;
  shippingCompanyName: string;
  shippingContactName: string;
  shippingPhoneNumber: string;
  comment: string | null;
  allowPartialShipments: boolean;
  shippingBillingType: string | null;
  shippingBillingAccountNumber: string | null;
  specialInstructions: string | null;
  subTotal: number;
}

interface PaintColorResponseBody {
  paintColors: PaintColor[];
}

interface PaintColor {
  paintColorId: number;
  hexCode: string;
  name: string;
}

interface SalesQuote {
  salesQuoteId: number;
  name: string;
  salesOrderId: number | null;
  createdUtc: string;
  receivedUtc: string | null;
}
